import { type BaseInstance, type ModelName } from '@pigello/pigello-matrix';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { getList } from '../api/get-list';
import { NESTED_KEY_SEPARATOR, QUERY_KEYS } from '../constants';
import type {
  ErrorResponse,
  ListResponse,
  QueryParams,
  useGetGenericListProps,
} from '../types';
import { customCaseHandlers } from './custom-cases';
import { filterNested, mergeDataWithNested } from './utils';
import { getQueryClient } from '@/providers/queryClient';
import { useFetchNestedQueries } from './use-fetch-nested';
import { useMemo } from 'react';

export type CreateListQueryKeyParams = {
  modelName: ModelName;
  queryParams: QueryParams<any>;
  overrideUrl?: string;
  overrideUrlReplaceAll?: boolean;
};

export function createListQueryKey(args: CreateListQueryKeyParams) {
  return [
    args.modelName,
    QUERY_KEYS.LIST,
    args.queryParams,
    args.overrideUrl,
    args.overrideUrlReplaceAll,
  ];
}

export function ensureGetList<T extends BaseInstance>(
  args: CreateListQueryKeyParams
) {
  const queryClient = getQueryClient();
  return queryClient.ensureQueryData({
    queryKey: createListQueryKey(args),
    queryFn: () => getList<T>(args),
  });
}

interface UseGetListOptions<TInstance extends BaseInstance>
  extends useGetGenericListProps<TInstance> {
  modelName: ModelName;
}

/**
 * Custom hook to fetch a list of items along with their nested relations.
 *
 * @template TInstance - The type of the base instance.
 * @param {UseGetListOptions<TInstance>} options - The options for fetching the list.
 * @returns {UseQueryResult<ListResponse<TInstance>, ErrorResponse>} The result of the query.
 */

export const useGetList = <TInstance extends BaseInstance>(
  options: UseGetListOptions<TInstance>
): UseQueryResult<ListResponse<TInstance>, ErrorResponse> => {
  const {
    modelName,
    nested = [],
    fetchAllManyRelations = false,
    queryParams,
    overrideUrl,
    overrideUrlReplaceAll,
    waitForAllNested,
    ...queryOptions
  } = options;
  /**
   * Filter out fields that should never be fetched to avoid unnecessary data fetching.
   */
  const nestedKey = nested.join(NESTED_KEY_SEPARATOR);
  const filteredNested = useMemo(
    () => filterNested<TInstance>(nestedKey),
    [nestedKey]
  );
  /**
   * React Query to fetch the main list based on the generated query keys.
   */
  const mainQuery = useQuery({
    queryKey: createListQueryKey({
      modelName,
      queryParams,
      overrideUrl,
      overrideUrlReplaceAll,
    }),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const res = await getList<TInstance>({
        modelName,
        queryParams,
        overrideUrl,
        overrideUrlReplaceAll,
        signal,
      });
      return res;
    },
    ...queryOptions,
  });

  const nestedQueries = useFetchNestedQueries(
    modelName,
    mainQuery.data?.list ?? [],
    filteredNested,
    fetchAllManyRelations
  );

  const mergedData = useMemo(() => {
    if (!mainQuery.data || Object.keys(nestedQueries.data).length < 1) {
      return mainQuery.data;
    }
    return mergeDataWithNested(
      mainQuery.data,
      nestedQueries.data,
      filteredNested
    );
  }, [filteredNested, mainQuery.data, nestedQueries.data]);

  /**
   * Apply custom case handlers if available for the given model.
   */
  const customizedDataResult = useQuery({
    queryKey: ['customizedData', modelName, JSON.stringify(mergedData)],
    queryFn: async () => {
      const handler = customCaseHandlers[modelName];
      if (!handler) {
        return mergedData;
      }
      try {
        const processedList = await handler({ data: mergedData?.list ?? [] });
        return {
          list: processedList.data,
          meta: mergedData?.meta,
        } as ListResponse<TInstance>;
      } catch {
        return mergedData;
      }
    },
    enabled:
      queryOptions.enabled !== false &&
      !!mergedData?.list.length &&
      !!customCaseHandlers[modelName],
  });

  /**
   * Determine if there is a custom case handler for the model.
   */
  const hasCustomCaseHandler = !!customCaseHandlers[modelName];

  const transformedData = useMemo(() => {
    if (hasCustomCaseHandler && customizedDataResult.data) {
      return customizedDataResult.data;
    }
    return mergedData;
  }, [customizedDataResult.data, hasCustomCaseHandler, mergedData]);
  /**
   * Combine the loading states based on the `waitForAllNested` flag.
   */
  const isLoading = waitForAllNested
    ? mainQuery.isLoading ||
      nestedQueries.isLoading ||
      customizedDataResult.isLoading
    : mainQuery.isLoading;
  const isFetching = waitForAllNested
    ? mainQuery.isFetching ||
      nestedQueries.isFetching ||
      customizedDataResult.isFetching
    : mainQuery.isFetching;
  const isPending = waitForAllNested
    ? mainQuery.isPending ||
      // TODO: check if we still needs this check assume its for when there is no nested to fetch
      (filteredNested.length > 0 && nestedQueries.isPending) ||
      customizedDataResult.isLoading
    : mainQuery.isPending;
  const isSuccess = waitForAllNested
    ? mainQuery.isSuccess ||
      nestedQueries.isSuccess ||
      customizedDataResult.isSuccess
    : mainQuery.isSuccess;

  /**
   * Return the combined query result including merged data and loading states.
   */
  return {
    data: transformedData,
    isLoading,
    isFetching,
    isPending,
    isSuccess,
  } as UseQueryResult<ListResponse<TInstance>, ErrorResponse>;
};
